import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { CockpitParty, DealAssistantAction } from '../../types';
import NudgeComposer from './NudgeComposer';

const SUGGESTIONS = [
  'What still needs to happen before we close?',
  'Draft a check-in to the buyer',
  "What's overdue or at risk?",
  'Summarize where this deal stands',
];

const Sparkle = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.9 5.8L20 10.7l-6.1 1.9L12 18.5l-1.9-5.9L4 10.7l6.1-1.9z" />
  </svg>
);

export default function AskAiPanel({ dealId, dealName, parties, onClose, onChanged }: {
  dealId: string; dealName?: string; parties: CockpitParty[]; onClose: () => void; onChanged: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<string | null>(null);
  const [actions, setActions] = useState<DealAssistantAction[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [composer, setComposer] = useState<{ party: CockpitParty; channel: 'sms' | 'email'; message: string; subject?: string } | null>(null);
  const [added, setAdded] = useState<Set<number>>(new Set());
  // TASK-AI (the 119th): per proposed action, how many of its tasks are saved and why the next one was refused.
  const [saved, setSaved] = useState<Record<number, number>>({});
  const [taskErr, setTaskErr] = useState<Record<number, string>>({});
  // The review (the 119th): the button is DISABLED while its save is out (a second press used to start a second save),
  // and a save from an EARLIER answer never writes onto the next one - each answer is numbered.
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const answerNo = useRef(0);

  const ask = async (q?: string) => {
    const text = (q ?? prompt).trim();
    if (!text) return;
    setPrompt(text);
    answerNo.current += 1;
    setBusy(true); setErr(null); setReply(null); setActions([]); setAdded(new Set()); setSaved({}); setTaskErr({}); setSaving(new Set());
    try {
      const r = await api.dealAssistant(dealId, text);
      setReply(r.reply); setActions(r.actions || []);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Ask AI failed'); }
    finally { setBusy(false); }
  };

  const openDraft = (a: DealAssistantAction) => {
    const p = parties.find((x) => x.contact_id === a.contact_id);
    if (!p) { setErr('That party is not on this deal.'); return; }
    setComposer({ party: p, channel: a.channel || 'sms', message: a.message || '', subject: a.subject });
  };
  /**
   * ⛔ TASK-AI (the 119th): a refused task said nothing (the loop had no catch) and a second press saved the tasks before
   *    it AGAIN. The reason shows beside the button with how many were saved, and a retry starts at the refused task.
   */
  const addTasks = async (a: DealAssistantAction, idx: number) => {
    const mine = answerNo.current;
    const current = () => answerNo.current === mine;
    setSaving((s) => new Set(s).add(idx));
    const tasks = a.tasks || [];
    const before = saved[idx] ?? 0;
    let done = before;
    setTaskErr((m) => { const next = { ...m }; delete next[idx]; return next; });
    try {
      // Pressed for these tasks: they finish saving even if he asks something else meanwhile.
      for (; done < tasks.length; done++) {
        await api.createDealTask(dealId, { title: tasks[done].title, due_date: tasks[done].due_date || undefined });
      }
      if (current()) setAdded((s) => new Set(s).add(idx));
    } catch (e) {
      if (current()) setTaskErr((m) => ({ ...m, [idx]: `Added ${done} of ${tasks.length}. ${e instanceof Error ? e.message : 'Could not add the task.'}` }));
    } finally {
      if (current()) {
        setSaved((m) => ({ ...m, [idx]: done }));
        setSaving((s) => { const next = new Set(s); next.delete(idx); return next; });
      }
      if (done > before) onChanged();
    }
  };
  const hasResult = busy || reply || err || actions.length > 0;

  return (
    <>
      <style>{`
        @keyframes aiFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes aiPop { from { opacity: 0; transform: translateY(10px) scale(.975) } to { opacity: 1; transform: none } }
        @keyframes aiRise { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
        @keyframes aiBlink { 0%,80%,100% { opacity:.25; transform: translateY(0) } 40% { opacity:1; transform: translateY(-2px) } }
        .ai-back { animation: aiFade .18s ease-out }
        .ai-panel { animation: aiPop .32s cubic-bezier(.16,1,.3,1) }
        .ai-rise { animation: aiRise .3s cubic-bezier(.16,1,.3,1) both }
        .ai-dot { width:6px;height:6px;border-radius:9999px;background:#111827;display:inline-block;animation:aiBlink 1.2s infinite ease-in-out }
      `}</style>
      <div className="ai-back fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/30 backdrop-blur-[3px]"
        onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <div className="ai-panel w-full max-w-[520px] bg-white rounded-[26px] overflow-hidden border border-black/5 shadow-[0_28px_70px_-18px_rgba(16,24,40,0.4)] flex flex-col max-h-[86vh]"
          onClick={(e) => e.stopPropagation()}>

          {/* header */}
          <div className="flex items-center gap-3 px-5 pt-4 pb-3">
            <div className="w-9 h-9 rounded-2xl bg-gray-900 text-white flex items-center justify-center shadow-sm">
              <Sparkle className="w-[18px] h-[18px]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-gray-900 tracking-tight leading-none">Ask AI</p>
              <p className="text-xs text-gray-400 truncate mt-1">{dealName || 'this deal'}</p>
            </div>
            <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto px-5">
            {!hasResult && (
              <div className="py-6">
                <h3 className="text-[22px] leading-tight font-semibold text-gray-900 tracking-tight">How can I help<br />with this deal?</h3>
                <p className="text-sm text-gray-400 mt-2">Ask about the escrow, or tell me to draft a message or add tasks.</p>
                <div className="mt-5 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)}
                      className="group text-left text-[13.5px] text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200/60 rounded-2xl px-4 py-2.5 transition-all hover:-translate-y-px flex items-center gap-2.5">
                      <Sparkle className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                      <span className="flex-1">{s}</span>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-all group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasResult && (
              <div className="py-4 space-y-4">
                {prompt && !busy && (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] bg-gray-900 text-white text-[13.5px] leading-relaxed rounded-[20px] rounded-tr-md px-4 py-2.5">{prompt}</div>
                  </div>
                )}
                {busy && (
                  <div className="flex items-center gap-2.5 text-gray-400 py-1">
                    <span className="ai-dot" /><span className="ai-dot" style={{ animationDelay: '.16s' }} /><span className="ai-dot" style={{ animationDelay: '.32s' }} />
                    <span className="text-[13px] ml-1">Thinking…</span>
                  </div>
                )}
                {err && <div className="ai-rise text-sm text-primary-500 bg-primary-100 border border-primary-200 rounded-2xl px-4 py-2.5">{err}</div>}
                {reply && (
                  <div className="ai-rise flex gap-2.5">
                    <div className="w-6 h-6 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0 mt-0.5"><Sparkle className="w-3 h-3" /></div>
                    <div className="text-[14px] text-gray-800 leading-relaxed whitespace-pre-wrap pt-0.5">{reply}</div>
                  </div>
                )}
                {actions.map((a, i) => (
                  <div key={i} className="ai-rise rounded-2xl border border-gray-200/80 bg-gray-50/40 p-3.5" style={{ animationDelay: `${0.05 * (i + 1)}s` }}>
                    {a.type === 'draft_message' ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" /><path d="M22 6l-10 7L2 6" /></svg>
                            Message · {a.channel}
                          </div>
                          <p className="text-[13.5px] font-medium text-gray-900 truncate">{a.contact_name || 'a party'}</p>
                          <p className="text-xs text-gray-400 truncate mt-0.5">{a.message}</p>
                        </div>
                        <button onClick={() => openDraft(a)} className="px-3.5 py-2 bg-gray-900 text-white rounded-full text-xs font-medium whitespace-nowrap hover:bg-gray-800 transition-colors">Review &amp; send</button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2">
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
                          {a.tasks?.length} task{(a.tasks?.length || 0) === 1 ? '' : 's'} to add
                        </div>
                        <div className="space-y-1.5 mb-3">
                          {a.tasks?.map((t, j) => (
                            <div key={j} className="flex items-center gap-2.5 text-[13.5px] text-gray-700">
                              <span className="w-3.5 h-3.5 rounded-[5px] border-[1.5px] border-gray-300 flex-shrink-0" />
                              <span className="flex-1">{t.title}</span>
                              {t.due_date && <span className="text-xs text-gray-400 tabular-nums">{t.due_date}</span>}
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button onClick={() => addTasks(a, i)} disabled={added.has(i) || saving.has(i)}
                            className={`px-3.5 py-2 text-white rounded-full text-xs font-medium hover:bg-gray-800 disabled:opacity-100 transition-colors ${added.has(i) ? 'bg-success' : 'bg-gray-900'}`}>
                            {added.has(i) ? 'Added ✓' : saving.has(i) ? 'Adding…' : 'Add these'}
                          </button>
                          {taskErr[i] && <p className="text-xs text-primary-500">{taskErr[i]}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* input */}
          <div className="px-4 pb-4 pt-2">
            <div className="flex items-end gap-2 bg-gray-50/70 border border-gray-200/80 rounded-[22px] px-3.5 py-2 transition-all focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-[0_2px_12px_-4px_rgba(16,24,40,0.12)]">
              <input value={prompt} autoFocus onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ask()}
                placeholder="Ask anything, or give it a task…" className="flex-1 bg-transparent outline-none text-[14px] text-gray-900 placeholder:text-gray-400 py-1.5" />
              <button onClick={() => ask()} disabled={busy || !prompt.trim()} aria-label="Ask"
                className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center flex-shrink-0 hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {composer && (
        <NudgeComposer dealId={dealId} party={composer.party} channel={composer.channel} initialMessage={composer.message} initialSubject={composer.subject} nextLabel={null} nextDate={null} onClose={() => setComposer(null)} />
      )}
    </>
  );
}
